<script setup>
import {useBaseStore} from "~/stores/base";
import Drawer from "~/components/Drawer.vue";
import {productReviewList} from "~/api/api.product";
import ModuleReviewsPicSlides from "~/components/review/ModuleReviewsPicSlides.vue";
import ModuleReview from "~/components/review/ModuleReview.vue";
import ModuleHeader from "~/components/review/ModuleHeader.vue";

let timer = null
const router = useRouter()
const route = useRoute()

/** emits **/
const emits = defineEmits([])

/** props **/
const props = defineProps({})

/** data **/
const open = ref(false)
const pageIndex = ref(1)
const pageSize = ref(20)
const reviews = ref([])
const selectedIndex = ref(-1)
const scrolled = ref(false)
const reviewsRef = ref(null)
const stickyRef = ref(null)
const observerActive = ref(true)

/** computed **/
const isMobile = computed(() => useBaseStore().getterIsMobile)
const userInfo = computed(() => useBaseStore().getterUserInfo)
const productId = computed(() => route.params.slug[0])
const hash = computed(() => route.hash)

/** watch **/
watch(hash, () => {
  if (hash.value.includes("#reviews-recommend")) {
    // 自动选择
    const options = {}
    const id = hash.value.split('-')[2]
    options.autoSelect = Number(id)

    getData(options)
    open.value = true
  } else {
    open.value = false
    reset()
  }
})
watch(reviewsRef, () => {
  if (!reviewsRef.value) return
  reviewsRef.value.addEventListener("scroll", (e) => {
    scrolled.value = reviewsRef.value.scrollTop > 0
  })
})

/** methods **/
const handleCloseDrawer = () => {
  // 获取当前路径，但去掉 hash 部分
  router.replace({
    path: router.currentRoute.value.path,
    query: router.currentRoute.value.query, // 保留查询参数
    hash: '' // 移除 hash
  });
}
const getData = async (options) => {
  const res = await productReviewList({
    id: productId.value,
    pageIndex: pageIndex.value,
    pageSize: pageSize.value,
    onlyImage: true
  })
  reviews.value = reviews.value.concat(res.result.list)
  if (res.result.pageIndex) {
    pageIndex.value++
    await getData()
  } else {
    // 拉到最后一页了
    await nextTick()
    if (options.autoSelect) handleSelect(options.autoSelect)
  }
}
const reset = () => {
  pageIndex.value = 1
  pageSize.value = 20
  reviews.value = []
  selectedIndex.value = -1
  scrolled.value = false
  observerActive.value = true
}
const scrollReviewIntoView = (index) => {
  // 关闭 moduleReview的observe
  clearTimeout(timer)
  observerActive.value = false

  /** 选择slide后主动滚动内容区 **/
  const reviewElements = reviewsRef.value.querySelectorAll('.module-review');

  const targetElement = reviewElements[index];
  const container = reviewsRef.value; // 父容器

  // 获取 sticky 的高度
  const stickyHeight = stickyRef.value.offsetHeight || 0;
  const margin = 10

  // 计算目标元素的相对位置
  const elementTop = targetElement.getBoundingClientRect().top;
  const containerTop = container.getBoundingClientRect().top;

  // 计算要滚动的距离，减去 header 的高度
  const scrollDistance = elementTop - containerTop + container.scrollTop - stickyHeight - margin;

  // 平滑滚动
  container.scrollTo({top: scrollDistance, behavior: 'smooth'});

  // 开启 moduleReview的observe
  timer = setTimeout(() => {
    // 滚动完成
    observerActive.value = true
  }, 1000)
}
const handleModuleReviewOnShow = index => {
  selectedIndex.value = index
}
const handleSelect = id => {
  const index = reviews.value.findIndex(review => review.id === id)
  selectedIndex.value = index
  scrollReviewIntoView(index)
}
const handleViewAll = () => {
  router.replace({
    path: router.currentRoute.value.path,
    query: router.currentRoute.value.query, // 保留查询参数
    hash: '#reviews-all'
  });
}

/** lifecycle **/
onMounted(() => {
  if (hash.value.includes("#reviews-recommend")) {
    // 自动选择
    const options = {}
    const id = hash.value.split('-')[2]
    options.autoSelect = Number(id)

    getData(options)
    open.value = true
  }
})
</script>

<template>
  <div class="drawer-reviews-recommend-mobile" v-if="isMobile">
    <Drawer :open="open" :position="'bottom'" :z-index="4" :global-header-visible="false" @close="handleCloseDrawer">
      <div class="reviews" ref="reviewsRef">
        <div class="sticky" ref="stickyRef" :class="{scrolled}">
          <module-header :title="'customer photos'" @close="handleCloseDrawer"/>
        </div>
        <div class="reviews-content">
          <module-review
            class="module-review"
            :observer-active="observerActive"
            :review="review"
            :index="index"
            v-for="(review, index) in reviews"
            :key="index"
            @show="handleModuleReviewOnShow"
          />
        </div>
        <div class="bottom">
          <module-reviews-pic-slides
            :reviews="reviews"
            :selected-index="selectedIndex"
            @select="handleSelect"
            @view-all="handleViewAll"
          />
        </div>
      </div>
    </Drawer>
  </div>
  <div class="drawer-reviews-recommend-desktop" v-else>
    <Drawer :open="open" :position="'right'" @close="handleCloseDrawer">
      <div class="reviews" ref="reviewsRef">
        <div class="sticky" ref="stickyRef" :class="{scrolled}">
          <module-header :title="'customer photos'" @close="handleCloseDrawer"/>
          <module-reviews-pic-slides
            :reviews="reviews"
            :selected-index="selectedIndex"
            @select="handleSelect"
            @view-all="handleViewAll"
          />
        </div>
        <div class="reviews-content">
          <module-review
            class="module-review"
            :observer-active="observerActive"
            :review="review"
            :index="index"
            v-for="(review, index) in reviews"
            :key="index"
            @show="handleModuleReviewOnShow"
          />
        </div>
      </div>
    </Drawer>
  </div>
</template>

<style scoped lang="scss">
@import "src/assets/config";

.drawer-reviews-recommend-mobile {
  .reviews {
    width: 100%;
    height: calc(100vh - $mobile-safari-address-bar-height);
    overflow-y: auto;

    display: flex;
    flex-direction: column;

    position: relative;

    &::-webkit-scrollbar {
      width: 0;
    }

    .sticky {
      position: sticky;
      top: 0;
      z-index: 1;

      display: flex;
      flex-direction: column;
      gap: 15px;

      background-color: white;
      transition: all .2s ease-in-out;
      margin-bottom: 10px;
    }

    .scrolled {
      filter: drop-shadow(0px 1px 20px rgba(0, 0, 0, 0.25));
    }

    .reviews-content {
      padding: 0 16px 150px 16px;
      display: flex;
      flex-direction: column;
      gap: 16px;
    }

    .bottom {
      width: 100%;
      position: fixed;
      bottom: 0;
      z-index: 1;
      filter: drop-shadow(0px 1px 20px rgba(0, 0, 0, 0.25));
    }
  }
}

.drawer-reviews-recommend-desktop {
  .reviews {
    width: 375px;
    height: calc(100vh - $desktop-header-height);
    overflow-y: auto;

    display: flex;
    flex-direction: column;

    position: relative;

    &::-webkit-scrollbar {
      width: 0;
    }

    .sticky {
      position: sticky;
      top: 0;
      z-index: 1;

      display: flex;
      flex-direction: column;
      gap: 15px;

      background-color: white;
      transition: all .2s ease-in-out;
      margin-bottom: 10px;
    }

    .scrolled {
      filter: drop-shadow(0px 1px 20px rgba(0, 0, 0, 0.25));
    }

    .reviews-content {
      padding: 0 16px 150px 16px;
      display: flex;
      flex-direction: column;
      gap: 16px;
    }
  }
}
</style>
